import React from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import {
  Item,
  SimpleSingleDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import DatePicker from '@grnhse/seedling/lib/birch/components/forms/date_picker';
import { AppliedOnType } from 'talent_matching/types';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { parseDate, formatDate } from 'talent_search/lib/utils';
import { subDays, subMonths, subYears } from 'date-fns';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix('talent_matching.filter_row.application_date');

const DATE_TYPE_OPTIONS = Object.values(AppliedOnType).map((value) => ({
  label: t(`range.${value}`),
  value,
}));

const DEFAULT_FIELD_NAMES = {
  start: 'appliedOnStart',
  end: 'appliedOnEnd',
  type: 'appliedOnType',
} as const;

type Props = {
  fieldNames?: {
    start: string;
    end: string;
    type: string;
  };
  helperText?: string;
};

const ApplicationDate = ({
  fieldNames = DEFAULT_FIELD_NAMES,
  helperText,
}: Props) => {
  const { userDateFormat } = useUserContext();
  const { setValue, watch } = useFormContext();
  const [appliedOnStart, appliedOnEnd, appliedOnType] = watch([
    fieldNames.start,
    fieldNames.end,
    fieldNames.type,
  ]);

  const startDate = parseDate(appliedOnStart, userDateFormat);
  const endDate = parseDate(appliedOnEnd, userDateFormat);

  const today = new Date();
  const yesterday = subDays(today, 1);
  const sevenDaysAgo = subDays(today, 7);
  const sixMonthsAgo = subMonths(today, 6);
  const twelveMonthsAgo = subMonths(today, 12);
  const twoYearsAgo = subYears(today, 2);

  const selectedType =
    DATE_TYPE_OPTIONS.find((option) => option.value === appliedOnType) || null;

  const showStartDate = [
    AppliedOnType.BETWEEN,
    AppliedOnType.ON_OR_AFTER,
  ].includes(appliedOnType);

  const showEndDate = [
    AppliedOnType.BETWEEN,
    AppliedOnType.ON_OR_BEFORE,
  ].includes(appliedOnType);

  const setDate = (field: string, newDate: Date | null) => {
    setValue(field, formatDate(newDate, userDateFormat), { shouldDirty: true });
  };

  const onTypeChange = (newType: Item | null) => {
    setValue(fieldNames.type, newType?.value ?? null, { shouldDirty: true });

    switch (newType?.value) {
      case AppliedOnType.TODAY:
        setDate(fieldNames.start, today);
        setDate(fieldNames.end, today);
        break;
      case AppliedOnType.YESTERDAY:
        setDate(fieldNames.start, yesterday);
        setDate(fieldNames.end, yesterday);
        break;
      case AppliedOnType.LAST_SEVEN_DAYS:
        setDate(fieldNames.start, sevenDaysAgo);
        setDate(fieldNames.end, today);
        break;
      case AppliedOnType.LAST_6_MONTHS:
        setDate(fieldNames.start, sixMonthsAgo);
        setDate(fieldNames.end, today);
        break;
      case AppliedOnType.LAST_12_MONTHS:
        setDate(fieldNames.start, twelveMonthsAgo);
        setDate(fieldNames.end, today);
        break;
      case AppliedOnType.LAST_2_YEARS:
        setDate(fieldNames.start, twoYearsAgo);
        setDate(fieldNames.end, today);
        break;
      default:
        setDate(fieldNames.start, null);
        setDate(fieldNames.end, null);
    }
  };

  return (
    <Stack spacing={2} direction="column">
      <SimpleSingleDropdown
        ariaLabel={t('range_placeholder')}
        placeholder={t('range_placeholder')}
        items={DATE_TYPE_OPTIONS}
        selectedItem={selectedType}
        onSelectedItemChange={onTypeChange}
        helperText={helperText}
      />
      {(showStartDate || showEndDate) && (
        <Stack spacing={2} direction="row">
          {showStartDate && (
            <DatePicker
              label={t('start')}
              onChange={(value) => setDate(fieldNames.start, value)}
              dateFormat={userDateFormat}
              selected={startDate}
              maxDate={today}
            />
          )}
          {showEndDate && (
            <DatePicker
              label={t('end')}
              onChange={(value) => setDate(fieldNames.end, value)}
              dateFormat={userDateFormat}
              selected={endDate}
              maxDate={today}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default ApplicationDate;
